import { ISubscribers } from 'features/ActivityFeed'
import { CarbonOffsetStrategy, Cargo } from 'features/BookingFlow'
import { Address, formatAddress, isSeaPort, placeToString, Port, PortOrAddress } from 'features/Directory'
import { delivery, pickup, ShipmentInterface, ShipmentMode, ShipmentType } from 'features/ShipmentDetails'
import { ITransportPlanInformation } from 'features/TransportPlan'
import { HttpBaseApiWrapper } from 'service/HttpBaseApiWrapper'
import { URLPrefix } from 'service/url'

import { ShipmentExport } from '../domain/ShipmentExport'
import { SortDirection } from './useShipmentListSort'

type SearchType = 'shipments' | 'messages' | 'more-results'
type TSearchIndex = 'shipments'
export interface ISearchOptions {
  size?: number
  from?: number
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- // TODO: Replace any with a suitable type
  filter?: any
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- // TODO: Replace any with a suitable type
  exactFilters?: any
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- // TODO: Replace any with a suitable type
  rangeFilter?: any
  sortBy?: string
  sortDirection?: SortDirection
  highlight?: boolean
  query?: string
}

interface IESAggregation<T> {
  key: T
  doc_count: number
}

interface IShipmentsAggregations {
  aggregations: {
    actionRequiredCount: {
      buckets: [IESAggregation<'requiredActionsCount'>]
    }
    state: {
      buckets: [
        IESAggregation<'new'>,
        IESAggregation<'active'>,
        IESAggregation<'completed'>,
        IESAggregation<'canceled'>,
      ]
    }
  }
  total: number
}

export interface ISearchResult<T> {
  hits: ITextSearchResult<T>[]
  total: number
}

export interface ISearchActivity {
  id: string
  type: string
  _createdAt: Date
  _internal: boolean
  referenceId: string
  customer_id: string
  // Message
  content?: string
}

export interface ITextSearchResult<T> {
  data: T
  highlight?: Partial<T>
}

export interface ITextSearchResults {
  shipments: ITextSearchResult<Partial<ISearchShipment>>[]
  messages: ITextSearchResult<ISearchActivity>[]
}

interface ITextSearchOptions {
  from?: number
  size?: number
  types: SearchType[]
}

export class SearchService {
  http: HttpBaseApiWrapper
  baseURL = '/search'
  constructor(http: HttpBaseApiWrapper) {
    this.http = http
  }
  search<T>(index: TSearchIndex, options: ISearchOptions): Promise<ISearchResult<T>> {
    const params = convertSearchOptionsToQueryParam(options)
    return this.http.get(`${this.baseURL}/literalTerm/${index}`, {
      urlPrefix: URLPrefix.Root,
      params,
    })
  }

  shipmentAggregations(): Promise<IShipmentsAggregations> {
    return this.http.get(`${this.baseURL}/shipments/aggregations`, {
      urlPrefix: URLPrefix.Root,
    })
  }

  aggregationsShipment(options: ISearchOptions): Promise<ShipmentExport> {
    const params = convertSearchOptionsToQueryParam(options)
    return this.http.get(`aggregations/containers`, {
      urlPrefix: URLPrefix.Root,
      params,
    })
  }

  textSearch(searchText: string, options: ITextSearchOptions): Promise<ITextSearchResults> {
    return this.http.get(this.baseURL, {
      urlPrefix: URLPrefix.Root,
      params: {
        query: searchText,
        ...options,
      },
    })
  }

  insights(query: string): Promise<void> {
    return this.http.get(`${this.baseURL}/insights?query=${query}`, {
      urlPrefix: URLPrefix.Root,
    })
  }

  // this gets the query param for getting total number of required action
  get requiredActionTotalQueryParam(): ISearchOptions {
    return {
      size: 1,
      from: 0,
      sortBy: '_id_num',
      sortDirection: SortDirection.ASC,
      exactFilters: { mode: ['sea', 'air', 'seaAir', 'rail'] },
      rangeFilter: { required_actions_count: { gte: 1 } },
    }
  }
}

interface ISearchShipmentContainer {
  container_number: string
}
interface ISearchPoint {
  port?: Port
  address?: Address
}

export interface ISearchShipment extends ITransportPlanInformation {
  id: string
  mode: ShipmentMode
  type: ShipmentType
  containers: ISearchShipmentContainer[]
  state: ShipmentInterface['state']
  _origin: ISearchPoint
  _destination: ISearchPoint
  customer_reference: string
  _cargo: Cargo[]
  required_actions_count: number
  eta?: string
  highlight?: Partial<ISearchShipment>
  pol?: string
  pod?: string
  destination: string
  origin: string
  shipper_contact_name: string
  booking_date?: string
  cargo_ready_date?: string
  carbonOffset?: CarbonOffsetStrategy
  subscribers?: ISubscribers
  shipmentIncidents?: ShipmentInterface['shipmentIncidents']
}

const convertLocationPointToSearchPoint = (portOrAddress: PortOrAddress): ISearchPoint => {
  return isSeaPort(portOrAddress) ? { port: portOrAddress } : { address: portOrAddress }
}

export const convertShipmentJsonToSearchShipment = (shipment: ShipmentInterface): ISearchShipment => {
  return {
    id: shipment._id,
    mode: shipment.mode,
    type: shipment.type,
    containers: shipment.containers.map((c) => ({
      container_number: c.containerNumber,
    })),
    state: shipment.state,
    _origin: convertLocationPointToSearchPoint(pickup(shipment)),
    _destination: convertLocationPointToSearchPoint(delivery(shipment)),
    // @ts-expect-error TS(2322) FIXME: Type 'string | undefined' is not assignable to typ... Remove this comment to see the full error message
    origin: placeToString(pickup(shipment)),
    // @ts-expect-error TS(2322) FIXME: Type 'string | undefined' is not assignable to typ... Remove this comment to see the full error message
    destination: placeToString(delivery(shipment)),
    // @ts-expect-error TS(2322) FIXME: Type 'string | undefined' is not assignable to typ... Remove this comment to see the full error message
    shipper_contact_name: shipment.shipperContact.companyName,
    // @ts-expect-error TS(2322) FIXME: Type 'string | undefined' is not assignable to typ... Remove this comment to see the full error message
    customer_reference: shipment.customerReference,
    _cargo: shipment.cargo || [],
    required_actions_count: shipment.requiredActions ? shipment.requiredActions.length : 0,
  }
}
export const searchPointToString = (searchPoint: ISearchPoint, short: boolean = false) => {
  if (searchPoint.address) {
    return formatAddress(searchPoint.address, short)
  }
  if (searchPoint.port) {
    return searchPoint.port.name
  }
  return ''
}
const convertSearchOptionsToQueryParam = (options: ISearchOptions) => {
  return {
    size: options.size == null ? 50 : options.size,
    from: options.from || 0,
    sortField: options.sortBy || '',
    sortDirection: options.sortDirection || '',
    filter: JSON.stringify(options.filter || {}),
    exactFilters: JSON.stringify(options.exactFilters || {}),
    rangeFilter: JSON.stringify(options.rangeFilter || {}),
    highlight: options.highlight || '',
    v2flow: false, // this is hardcoded for old SHIP to prevent the UI from breaking
    ...(options.query && { query: options.query }),
  }
}
